import { STORYCRAFT_ANTHROPIC_MODEL, STORYCRAFT_OPENAI_MODEL } from "./storyCraftModels";

export const OPENAI_CHAT_MODELS = [
  { value: "gpt-5.5", label: "GPT-5.5" },
  { value: "gpt-5.3-codex", label: "GPT-5.3 Codex" },
  { value: "gpt-5.4", label: "GPT-5.4" },
  { value: "gpt-5.4-mini", label: "GPT-5.4 Mini" },
  { value: "gpt-5.4-nano", label: "GPT-5.4 Nano" },
  { value: "gpt-5.2", label: "GPT-5.2" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5" },
  { value: "o1-preview", label: "o1-preview" },
  { value: "o1-mini", label: "o1-mini" },
] as const;

export const ANTHROPIC_CHAT_MODELS = [
  { value: "claude-opus-4-8", label: "Claude Opus 4.8" },
  { value: "claude-opus-4-7", label: "Claude Opus 4.7" },
  { value: "claude-sonnet-5", label: "Claude Sonnet 5" },
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { value: "claude-haiku-4-5", label: "Claude Haiku 4.5" },
  { value: "claude-opus-4-20250514", label: "Claude Opus 4" },
  { value: "claude-sonnet-4-20250514", label: "Claude Sonnet 4" },
  { value: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku" },
] as const;

export type OpenAiChatModel = (typeof OPENAI_CHAT_MODELS)[number]["value"];
export type AnthropicChatModel = (typeof ANTHROPIC_CHAT_MODELS)[number]["value"];
export type LlmModelValue = OpenAiChatModel | AnthropicChatModel;

const ALL_MODEL_VALUES = new Set<string>([
  ...OPENAI_CHAT_MODELS.map((m) => m.value),
  ...ANTHROPIC_CHAT_MODELS.map((m) => m.value),
]);

export function isKnownLlmModel(value: string): value is LlmModelValue {
  return ALL_MODEL_VALUES.has(value);
}

export function llmProviderForModel(value: string): "openai" | "anthropic" {
  return ANTHROPIC_CHAT_MODELS.some((m) => m.value === value) ? "anthropic" : "openai";
}

export const DEFAULT_CHAT_MODEL: LlmModelValue = "gpt-4o-mini";

export const DEFAULT_CHAT_MODEL_STORAGE_KEY = "desktop-v2.defaultChatModel";

export function readPersistedDefaultChatModel(): LlmModelValue {
  if (typeof window === "undefined") return DEFAULT_CHAT_MODEL;
  try {
    const raw = window.localStorage.getItem(DEFAULT_CHAT_MODEL_STORAGE_KEY);
    if (raw && isKnownLlmModel(raw)) return raw;
  } catch {
    // ignore
  }
  return DEFAULT_CHAT_MODEL;
}

export function writePersistedDefaultChatModel(model: LlmModelValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DEFAULT_CHAT_MODEL_STORAGE_KEY, model);
  } catch {
    // ignore
  }
}

export const DEFAULT_CHARACTER_EXTRACT_MODEL: LlmModelValue = "claude-sonnet-5";

export const CHARACTER_EXTRACT_MODEL_STORAGE_KEY = "desktop-v2.characterExtractModel";

export function readPersistedCharacterExtractModel(): LlmModelValue {
  if (typeof window === "undefined") return DEFAULT_CHARACTER_EXTRACT_MODEL;
  try {
    const raw = window.localStorage.getItem(CHARACTER_EXTRACT_MODEL_STORAGE_KEY);
    if (raw && isKnownLlmModel(raw)) return raw;
  } catch {
    // ignore
  }
  return DEFAULT_CHARACTER_EXTRACT_MODEL;
}

export function writePersistedCharacterExtractModel(model: LlmModelValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CHARACTER_EXTRACT_MODEL_STORAGE_KEY, model);
  } catch {
    // ignore
  }
}

export const DEFAULT_STORYCRAFT_MODEL: LlmModelValue = STORYCRAFT_ANTHROPIC_MODEL as LlmModelValue;

export const STORYCRAFT_MODEL_STORAGE_KEY = "desktop-v2.storyCraftModel";

export function readPersistedStoryCraftModel(): LlmModelValue {
  if (typeof window === "undefined") return DEFAULT_STORYCRAFT_MODEL;
  try {
    const raw = window.localStorage.getItem(STORYCRAFT_MODEL_STORAGE_KEY);
    if (raw && isKnownLlmModel(raw)) return raw;
  } catch {
    // ignore
  }
  return DEFAULT_STORYCRAFT_MODEL;
}

export function writePersistedStoryCraftModel(model: LlmModelValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORYCRAFT_MODEL_STORAGE_KEY, model);
  } catch {
    // ignore
  }
}

export function resolveStoryCraftModelId(
  provider: "openai" | "anthropic",
  preferred?: string
): string {
  if (typeof preferred === "string" && preferred.trim()) return preferred.trim();
  return provider === "anthropic" ? STORYCRAFT_ANTHROPIC_MODEL : STORYCRAFT_OPENAI_MODEL;
}
